import React, { useCallback, useMemo } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Alerts, InlineAlert, Logo, BUSINESS_UNIT_ROLLWORKS } from '@nextroll/ar-style-base';

import { CrmOptionType } from 'types/rofl';
import { IWorkflowAccountActionContactFilter } from 'types/roflWorkflowActions';

import { AccountActionContactsMaxSection } from './AccountActionNodeSection/AccountActionContactsMaxSection';
import { AccountActionCrmContactFilterSection } from './AccountActionNodeSection/AccountActionCrmContactFilterSection';
import { AccountActionCrmRecordTypeSection } from './AccountActionNodeSection/AccountActionCrmRecordTypeSection';
import { AccountActionCrmNoConnectedSection } from './AccountActionNodeSection/AccountActionCrmNoConnectedSection';
import { AccountActionCrmMappingSection } from './AccountActionNodeSection/AccountActionCrmMappingSection';
import { AccountActionCrmHeaderSection } from './AccountActionNodeSection/AccountActionCrmHeaderSection';
import { ActionNodeEmailPanel } from '../ActionNode/ActionNodePanel/ActionNodeEmailPanel';


/**
 * utility methods that used in constructing the select page content of the account action node panel
 */
const AccountActionSelectPanel = () => (
    <>
        <div data-testid='node-header-section' className='workflow-node-panel-workflow-action-header-container'>
            <div style={{ margin: '4px 12px 4px 0' }}>
                <Logo
                    i18n={i18n}
                    businessUnit={BUSINESS_UNIT_ROLLWORKS}
                    height='16px'
                    wordmark={false}
                    disableRollWorksTheme
                />
            </div>
            <h4>AdRoll</h4>
        </div>
        <input
            type='radio'
            id='account-action-select-radio-button'
            name='serviceWorkflowAccountActionSelection'
            className='ar-radio-button'
            value='redeem'
            checked
            onChange={() => {}}
        />
        <label htmlFor='account-action-select-radio-button'>
            {i18n.gettext('Acquire New Contacts')}
            <br />
            <small style={{ marginLeft: -6 }}>
                {i18n.gettext('This action uses one credit per contact from your annually allocated quota')}
            </small>
        </label>
    </>
);
export const useAccountActionNodeCardConfigSelect = () => [
    {
        key: 'workflow-action-node-panel-element-account-action-select',
        component: AccountActionSelectPanel,
    },
];

/**
 * utility methods that are used in selecting destination page content e.g. 'crm' or 'email'
 */
interface IAccountDestinationSelectPanelProps {
    crm?: CrmOptionType;
    destination: string;
    updateDestination: (dest: string) => void;
}
const AccountDestinationSelectPanel = (props: IAccountDestinationSelectPanelProps) => (
    <>
        <div data-testid='node-header-section' className='workflow-node-panel-workflow-action-header-container'>
            <div style={{ margin: 'auto 7px' }}><i className={`fab fa-${props.crm}`} aria-hidden /></div>
            {props.crm === 'salesforce' && <h4>Salesforce</h4>}
            {props.crm === 'hubspot' && <h4>HubSpot</h4>}
        </div>
        <input
            type='radio'
            id='account-destination-crm-select-radio-button'
            className='ar-radio-button'
            value='crm'
            checked={props.destination === 'crm'}
            onChange={() => props.updateDestination('crm')}
        />
        <label htmlFor='account-destination-crm-select-radio-button'>
            {i18n.gettext('Create Contact / Lead Record')}
        </label>
        <div data-testid='node-header-section' className='workflow-node-panel-workflow-action-header-container'>
            <div style={{ margin: 'auto 7px' }}><i className='fas fa-envelope' aria-hidden /></div>
            <h4>{i18n.gettext('Email')}</h4>
        </div>
        <input
            type='radio'
            id='account-destination-email-select-radio-button'
            className='ar-radio-button'
            value='email'
            checked={props.destination === 'email'}
            onChange={() => props.updateDestination('email')}
        />
        <label htmlFor='account-destination-email-select-radio-button'>
            {i18n.gettext('Export via CSV in an Email')}
        </label>
    </>
);
export const useAccountActionNodeCardDestinationSelect = (props: IAccountDestinationSelectPanelProps) => [
    {
        key: 'workflow-action-node-panel-element-account-destination-select',
        component: () => AccountDestinationSelectPanel(props),
        isRequired: true,
        validate: () => !!props.destination,
    },
];


/**
 * utility methods that used in constructing the page content of the account action node panel
 */
const AccountActionContactsPanelDescription = () => (
    <div className='workflow-node-panel-header-container'>
        <div style={{ margin: '4px 12px 4px 0' }}>
            <Logo
                i18n={i18n}
                businessUnit={BUSINESS_UNIT_ROLLWORKS}
                height='16px'
                wordmark={false}
                disableRollWorksTheme
            />
        </div>
        <div className='workflow-node-panel-header-container-help-section'>
            <h4>{i18n.gettext('AdRoll: Acquire New Contacts')}</h4>
            <div className='workflow-node-panel-header-container-help-section-text'>
                {i18n.gettext('Each contact added to your CRM deducts one credit from your plan. If your credits are depleted, contact acquisition will pause until you obtain more credits.')}
            </div>
            <a href='https://help.rollworks.com/hc/articles/26202476299405' rel='noopener noreferrer' target='_blank'>
                {i18n.gettext('AdRoll Help Center: Contact Discovery Guide')}
                &nbsp;
                <i className='ar-icon ar-icon-fw fa-external-link-alt' />
            </a>
        </div>
    </div>
);

export const useAccountActionNodeCardConfigRollWorks = (props: {
    maxContacts: number;
    onMaxContactsUpdate?: (value: number) => void;
    showError?: boolean;

    // getter and setter of the contact filter rules
    rules?: (IWorkflowAccountActionContactFilter | { rules: IWorkflowAccountActionContactFilter[] })[]; // it can be nested some time
    onContactFilterUpdate?: (rules: IWorkflowAccountActionContactFilter[]) => void;

    // getter and setter of the activity query value
    activityQuery?: boolean;
    onActivityQueryUpdate?: (activityQuery: boolean) => void;
}) => {
    const {
        maxContacts,
        onMaxContactsUpdate,
        showError,
        rules,
        onContactFilterUpdate,
        activityQuery,
        onActivityQueryUpdate,
    } = props;

    const sectionContactsMaxPerCompany = useCallback(() => (
        <AccountActionContactsMaxSection
            value={maxContacts}
            onUpdate={onMaxContactsUpdate}
            showError={showError}
        />
    ), [maxContacts, onMaxContactsUpdate, showError]);

    const sectionContactsFilters = useCallback(() => (
        <AccountActionCrmContactFilterSection
            rules={rules}
            onUpdateRules={onContactFilterUpdate}
            activityQuery={activityQuery}
            onUpdateActivityQuery={onActivityQueryUpdate}
        />
    ), [rules, onContactFilterUpdate, activityQuery, onActivityQueryUpdate]);

    return [
        {
            key: 'workflow-action-node-panel-element-account-action-contact-description',
            component: AccountActionContactsPanelDescription,
        },
        {
            key: 'workflow-action-node-panel-element-account-action-contact-max-per-company',
            component: sectionContactsMaxPerCompany,
            isRequired: true,
            validate: () => /^[1-9]\d*$/.test(`${maxContacts}`),
        },
        {
            key: 'workflow-action-node-panel-element-account-action-select-filters',
            component: sectionContactsFilters,
            isRequired: true,
        },
    ];
};


/**
 * utility methods that used in constructing the CRM page content of the account action node panel
 */
export const useAccountActionNodeCardConfigCrm = (props: {
    advertisableEid?: string;
    crm?: CrmOptionType;
    updateCrm: (advertisableEid?: string) => void;

    recordType?: string;
    onUpdateRecordType?: (recordType: string) => void;

    noCrmConnected: boolean;
    noCrmAuth: boolean;
    hasFieldMappingError: boolean;
    hasFieldMappingWarning: boolean;
    showError?: boolean;
}) => {
    const {
        advertisableEid, crm, updateCrm, recordType, onUpdateRecordType,
        noCrmConnected, noCrmAuth, hasFieldMappingError, hasFieldMappingWarning, showError,
    } = props;

    // action "crm" > the header section
    const sectionDetailsPanelHeader = useCallback(() => <AccountActionCrmHeaderSection crm={crm ?? 'other'} />, [crm]);

    // action "crm" > section when no CRM connected or CRM = other
    const sectionDetailsPanelNoCrm = useCallback(
        () => <AccountActionCrmNoConnectedSection advertisableEid={advertisableEid} crm={crm} updateCrm={updateCrm} />,
        [advertisableEid, crm, updateCrm],
    );

    // action "crm" > section when no authentication connection that corresponds to the selected CRM
    const sectionDetailsPanelNoAuth = useCallback(() => (
        <InlineAlert
            type={Alerts.ALERT_TYPE_ERROR}
            title={i18n.gettext('Authentication error')}
            message={(
                <>
                    {crm === 'salesforce' && i18n.gettext('The integration user for Salesforce is no longer valid. Please re-authenticate the integration.')}
                    {crm === 'hubspot' && i18n.gettext('The integration user for HubSpot is no longer valid. Please re-authenticate the integration.')}
                    <br />
                    <a
                        role='button'
                        href={`/settings/salesforce?advertisable=${advertisableEid}`}
                        target='_blank'
                        rel='noreferrer noopener'
                        className='btn btn-default mt-2'
                    >
                        {i18n.gettext('Go to Settings')}
                    </a>
                </>
            ) as any}
            showIcon
        />
    ), [advertisableEid, crm]);

    const sectionDetailsPanelRecordType = useCallback(() => (
        <AccountActionCrmRecordTypeSection
            crm={crm}
            value={recordType}
            onUpdate={onUpdateRecordType}
            showDescription={!recordType && !hasFieldMappingError && !hasFieldMappingWarning}
            showError={showError}
        />
    ), [
        crm, recordType, onUpdateRecordType,
        hasFieldMappingError, hasFieldMappingWarning, showError,
    ]);
    const sectionDetailsPanelMapping = useCallback(() => (
        <AccountActionCrmMappingSection
            advertisableEid={advertisableEid}
            crm={crm}
            value={recordType}
            isError={hasFieldMappingError}
            isWarning={!hasFieldMappingError && hasFieldMappingWarning}
        />
    ), [advertisableEid, crm, recordType, hasFieldMappingError, hasFieldMappingWarning]);

    return [
        {
            key: 'workflow-action-node-panel-element-account-action-details-description',
            component: sectionDetailsPanelHeader,
        },
        {
            key: 'workflow-action-node-panel-element-account-action-details-no-crm',
            component: sectionDetailsPanelNoCrm,
            isHidden: !noCrmConnected,
            validate: () => !noCrmConnected,
        },
        {
            key: 'workflow-action-node-panel-element-account-action-details-no-auth',
            component: sectionDetailsPanelNoAuth,
            isHidden: !noCrmAuth,
            validate: () => !noCrmAuth,
        },
        {
            key: 'workflow-action-node-panel-element-account-action-details-record-type',
            component: sectionDetailsPanelRecordType,
            isRequired: true,
            isHidden: noCrmConnected || noCrmAuth,
            validate: () => !!recordType,
        },
        {
            key: 'workflow-action-node-panel-element-account-action-details-field-mapping',
            component: sectionDetailsPanelMapping,
            isRequired: true,
            isHidden: noCrmConnected || noCrmAuth || !recordType,
            validate: () => !hasFieldMappingError,
        },
    ];
};

export const useAccountActionNodeCardConfigEmail = (props: {
    advertisableEid: string;
    selectedEmails: string[];
    onUpdateEmails: (emails: string[]) => void;
}) => {
    const { advertisableEid, selectedEmails, onUpdateEmails } = props;

    const actionNodeEmailPanel = useMemo(() => (
        <ActionNodeEmailPanel
            advertisableEid={advertisableEid}
            title={i18n.gettext('Email recipients')}
            customHeader={<AccountActionCrmHeaderSection crm='email' />}
            hideFooter
            emails={selectedEmails}
            hideSalesInsightEmails
            onUpdateEmails={onUpdateEmails}
        />
    ), [advertisableEid, selectedEmails, onUpdateEmails]);

    return [
        {
            key: 'workflow-action-node-panel-element-account-action-email',
            component: () => actionNodeEmailPanel,
            isRequired: true,
            validate: () => selectedEmails.length > 0,
        },
    ];
};
